import { Injectable } from '@angular/core';
import { pickBy } from 'lodash';
import { combineLatest, ConnectableObservable, Observable, of, ReplaySubject } from 'rxjs';
import { map, pluck, shareReplay } from 'rxjs/operators';
import { ApiService } from '../api-service';
import { ApiDefaults } from '../models/api-defaults.constants';
import { FilterRequest, ListResponse, SortDirectionEnterprise } from '../models/request-response';
import {
    Mk2ListRequest,
    Mk2Response,
    Pagination as ProtoPagination,
    ProtoListRequest,
    ProtoResponse,
    SortDirectionFinancing
} from '../models/request-response-all-java';
import { LsqListSource } from './lsq-list-source';

function asRequestWithNestedPagination(
    data: Partial<FilterRequest | ProtoListRequest>
): ProtoListRequest {
    return data as ProtoListRequest;
}
function asFilterRequest(data: Partial<FilterRequest | ProtoListRequest>): FilterRequest {
    return data as FilterRequest;
}

type Pagination = ProtoPagination | FilterRequest;
@Injectable({ providedIn: 'root' })
export class LsqApiServiceListSource<
    ResponseType extends ListResponse<any> | ProtoResponse | Mk2Response = ListResponse<any>,
    RequestType extends FilterRequest | ProtoListRequest | Mk2ListRequest = FilterRequest,
    RowType = any
> extends LsqListSource<RowType> {
    public request: RequestType;
    public request$ = new ReplaySubject<RequestType>(1);
    public rows$: Observable<RowType[]> | ConnectableObservable<RowType[]>;
    public data$: Observable<ResponseType> | ConnectableObservable<ResponseType>;

    constructor(
        private _apiService: ApiService<RequestType, ResponseType>,
        initialRequest?: RequestType,
        rowMapper = (row: RowType) => row
    ) {
        super({
            data$: _apiService.dataStream.pipe(shareReplay(1)),
            rows$: _apiService.dataStream.pipe(
                pluck('rows'),
                map((rows) => rows.map(rowMapper)),
                shareReplay(1)
            ),
            totalCount$: _apiService.dataStream.pipe(
                map(({ totalCount }) => totalCount),
                shareReplay(1)
            ),
            loading$: _apiService.isInFlightStream.pipe(shareReplay(1)),
            error$: _apiService.errorStream.pipe(shareReplay(1)),
            noData$: combineLatest([_apiService.dataStream, _apiService.isInFlightStream]).pipe(
                map(([{ rows }, isInFlight]) => !isInFlight && (!rows || !rows.length)),
                shareReplay(1)
            )
        });

        if (initialRequest) {
            this.dispatchRequest(initialRequest as RequestType);
        }
    }

    public dispatchRequest(newRequest: Partial<RequestType> = {}): void {
        if (!this.request) {
            this.request = newRequest as RequestType;
        }
        this._updateRequest(newRequest);
        this.request$.next(this.request);
        // TODO: The fact that this has no `replay` but doesn't break anything suggests that it's
        // maybe not being used. Explore deprecating `ApiService#requestStream` altogether.
        this._apiService.setRequestStream(of(this.request));
        this._apiService.execute(this.request);
    }

    public handleSkipChange = (skip: number) => {
        const isRequestWithNestedPagination = this._isRequestWithNestedPagination(this.request);
        const currentSkip = isRequestWithNestedPagination
            ? asRequestWithNestedPagination(this.request).pagination.skip
            : asFilterRequest(this.request).skip;

        if (!!this.request && currentSkip !== skip) {
            this._setRequestWithNewPagination({ skip });
            this.dispatchRequest(this.request as RequestType);
        }
    };

    public handleTakeChange = (take: number) => {
        const currentTake = this._isRequestWithNestedPagination(this.request)
            ? asRequestWithNestedPagination(this.request).pagination.take
            : asFilterRequest(this.request).take;
        if (currentTake !== take) {
            this._setRequestWithNewPagination({ take });
            this.dispatchRequest(this.request as RequestType);
        }
    };

    public handleSort = ({
        sortColumn,
        sortDirection
    }: {
        sortColumn: string;
        sortDirection: SortDirectionEnterprise | SortDirectionFinancing;
    }) => {
        if (
            this._isRequestWithNestedPagination(this.request) &&
            sortDirection === SortDirectionEnterprise.Asc
        ) {
            sortDirection = SortDirectionFinancing.ASCENDING;
        } else if (
            this._isRequestWithNestedPagination(this.request) &&
            sortDirection === SortDirectionEnterprise.Desc
        ) {
            sortDirection = SortDirectionFinancing.DESCENDING;
        }

        const currentSortColumn = this._isRequestWithNestedPagination(this.request)
            ? asRequestWithNestedPagination(this.request).pagination.sortColumn
            : asFilterRequest(this.request).sortColumn;
        const currentSortDirection = this._isRequestWithNestedPagination(this.request)
            ? asRequestWithNestedPagination(this.request).pagination.sortDirection
            : asFilterRequest(this.request).sortDirection;
        if (currentSortColumn !== sortColumn || currentSortDirection !== sortDirection) {
            this._setRequestWithNewPagination({ sortColumn, sortDirection, skip: 0 });
            this.dispatchRequest(this.request as RequestType);
        }
    };

    private _setRequestWithNewPagination(paginationUpdate: Partial<Pagination>): void {
        const originalRequest = this.request;
        const isRequestWithNestedPagination = this._isRequestWithNestedPagination(originalRequest);
        const updatedPagination = this._createUpdatedPagination(paginationUpdate);

        if (isRequestWithNestedPagination) {
            this._updateRequest({ pagination: updatedPagination } as RequestType);
        } else {
            this._updateRequest(updatedPagination as RequestType);
        }
    }

    private _updateRequest(requestUpdate: Partial<RequestType>): void {
        const originalRequest = this.request;
        const isRequestWithNestedPagination = this._isRequestWithNestedPagination(originalRequest);
        const updatedPagination = this._createUpdatedPagination(this._getPagination(requestUpdate));

        this.skip = updatedPagination.skip;
        this.take = updatedPagination.take;
        this.sortColumn = updatedPagination.sortColumn;
        this.sortDirection = (updatedPagination.sortDirection as unknown) as SortDirectionFinancing;

        if (isRequestWithNestedPagination) {
            this.request = {
                ...originalRequest,
                ...requestUpdate,
                pagination: updatedPagination
            };
        } else {
            this.request = {
                ...originalRequest,
                ...requestUpdate,
                ...updatedPagination
            };
        }
    }

    private _createUpdatedPagination(paginationUpdate: Partial<Pagination>): Pagination {
        const originalRequest = this.request;
        const originalPagination = this._getPagination(originalRequest as RequestType);

        return {
            skip: 0,
            take: ApiDefaults.TakeDefault,
            ...originalPagination,
            ...paginationUpdate
        } as Pagination;
    }

    private _getPagination(request: Partial<RequestType>): Pagination {
        const isRequestWithNestedPagination = this._isRequestWithNestedPagination(
            request as RequestType
        );
        const pagination = isRequestWithNestedPagination
            ? asRequestWithNestedPagination(request).pagination
            : asFilterRequest(request);
        return pickBy(pagination, (value) => typeof value !== 'undefined') as Pagination;
    }

    private _isRequestWithNestedPagination(
        request: FilterRequest | ProtoListRequest | Mk2ListRequest
    ): request is ProtoListRequest {
        return !!asRequestWithNestedPagination(request).pagination;
    }
}
